<template>
  <main id="content" aria-labelledby="main-title">
    
    <ElementHeroStats :stats="stats" v-if="page" :content="page" />

    <Section margins="reset-top">
      <Container>

        <ElementModal v-if="sent" type="success" title="Thanks. Your message has been sent" message="We will get back to you within one business day." />
        
        <FadeIn>

          <div v-if="sent" class="mx-auto max-w-xl text-center">
            <p class="text-subtitle">Thanks. Your message has been sent.</p>
          </div>

          <form v-else action="#" method="POST" class="mx-auto my-16 max-w-xl sm:mt-20">
            <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <label for="name" class="text-lg font-bold text-gray-900">Full name</label>
                <div class="mt-2.5">
                  <input type="text" v-model="name" name="name" id="name" @focus="formTracking.start()" placeholder="Your Name" autocomplete="name" class="input-text" />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label for="organization" class="text-lg font-bold text-gray-900">Organisation</label>
                <div class="mt-2.5">
                  <input type="text" v-model="organization" id="organization" @focus="formTracking.start()" placeholder="Organisation Name" autocomplete="organization" class="input-text" />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label for="email" class="text-lg font-bold text-gray-900">Work email</label>
                <div class="mt-2.5">
                  <input type="email" v-model="email" id="email" @focus="formTracking.start()" autocomplete="email" placeholder="yourname@example.com" class="input-text" />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label for="phone" class="text-lg font-bold text-gray-900">Phone (optional)</label>
                <div class="mt-2.5">
                  <input type="text" v-model="phone" id="phone" @focus="formTracking.start()" autocomplete="phone" placeholder="Phone number" class="input-text" />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label for="message" class="text-lg font-bold text-gray-900">Message</label>
                <div class="mt-2.5">
                  <textarea id="message" name="message" v-model="message" rows="8" @focus="formTracking.start()"  placeholder="Enter your comments" class="input-text" />
                </div>
              </div>
              <!-- Honeypot field - hidden from users, bots will fill it -->
              <div class="sm:col-span-2" aria-hidden="true" style="position: absolute; left: -9999px;">
                <label for="website">Website</label>
                <input type="text" v-model="website" id="website" name="website" tabindex="-1" autocomplete="off" />
              </div>
            </div>
            <div class="mt-6">
              <div class="flex">
                <input 
                @click="errorMessage = null"
                id="consent" 
                v-model="consent" 
                type="checkbox" 
                class="h-5 w-5 mt-[2px] cursor-pointer rounded border-[#E4E2DF] text-[#4d5ceb] focus:ring-transparent"
                />
                <label @click="errorMessage = null" for="consent" class="ml-2 block text-sm cursor-pointer">
                  I confirm I have read and agree to the processing of my above personal data as described in the <NuxtLink :to="$localePath('/policies/privacy')" class="underline" target="_blank">Privacy Policy</NuxtLink>.
                </label>
              </div> 
            </div>
            <div class="mt-6">
              <button
              type="submit"
              :disabled="waiting"
              @click="submitForm"
              class="inline-flex items-center justify-center rounded-md border border-transparent bg-[#096b72] px-5 py-3 text-md font-semibold text-white hover:bg-[#07484b]"
              :class="{ 'opacity-60' : waiting }"
              >
                <template v-if="waiting">
                  <ElementLoadingSpinner />
                  <span>{{ sendingMessage || 'Sending...' }}</span>
                </template>
                <template v-else>
                  Send message
                </template>
              </button>
              <ElementSubmitStatus :message="waiting ? sendingMessage : ''" />
            </div>
            <div class="mt-6" v-if="errorMessage"><p class="text-[#DE1A1A] text-sm">{{ errorMessage }}</p></div>
          </form>
        </FadeIn>

      </Container>
    </Section>

    <template v-if="page?.dynamic">
      <template v-for="(section, index) in page?.dynamic">
        <component 
        :is="getSectionComponent(section.collection)" 
        :sectionIndex="index" 
        :content="section.item"
        :sectionTotal="page.dynamic.length"
        />
      </template>
    </template>

    <ElementBookDemoCta />

  </main>
</template>

<script setup>
const route = useRoute()
const contentSections = useContentSections()
const userStore = useUserStore()

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const name = ref('')
const email = ref('')
const phone = ref('')
const organization = ref('')
const message = ref('')
const errorMessage = ref(null)
// Funnel tracking for the main contact form (the site's primary lead form).
const formTracking = useFormTracking('contact')
const consent = ref(false)
const waiting = ref(null)
const website = ref('') // Honeypot field
const formLoadedAt = ref(Date.now()) // Timestamp for spam detection

const sent = ref(false)

const { getItems } = useCms()
const fields = useFields()
const { data } = await useAsyncData(
  currentLocale.value+'-page-contact',
  () => getItems({
    collection: 'contact',
    params: {
      fields
    },
  })
)

const page = data?.value

const seo = computed(() => {
  if (page?.seo) {
    return useSeo(page.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

const getSectionComponent = key => {
  return contentSections[key]
}

const submitForm = async (e) => {
  e.preventDefault()
  waiting.value = true
  errorMessage.value = null
  formTracking.submitted()

  if (!name.value || !email.value || !message.value) {
    errorMessage.value = 'Please fill out all fields before submitting the form.'
    formTracking.failed('missing-required-fields')
    waiting.value = null
    return false
  }

  if (consent.value !== true) {
    errorMessage.value = 'To continue, please confirm we can use the above personal data in accordance with our Privacy Policy.'
    formTracking.failed('consent-not-given', 'consent')
    waiting.value = null
    return false
  }

  const data = {
    name: name.value,
    email: email.value,
    phone: phone.value,
    organization: organization.value,
    'marketing_status': 'subscribed',
    message: '<p>Message submitted from Contact page:</p>'+message.value,
    webformId: '59',
    utmSource: userStore.utmSource,
    utmMedium: userStore.utmMedium,
    utmCampaign: userStore.utmCampaign,
    utmLandingPage: userStore.utmLandingPage,
    // Conversion attribution (paid click ids + GA4 client id) for server-side
    // conversion firing.
    ...useConversionFields(),
    // Spam protection fields
    website: website.value,
    formLoadedAt: formLoadedAt.value
  }

  startSendingMessages()

  try {

    const addUserRes = await $fetch('/api/elkerContact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (addUserRes.ok) {

      sent.value = true
      // recorded === false is a server-side spam decoy; count it as a submit
      // that succeeded for the user but flag it so it is separable from real leads.
      formTracking.ok({ recorded: addUserRes.recorded !== false })

      // recorded: false marks a server-side decoy (spam gate tripped): show
      // the success state as normal but do not fire conversion events for it.
      if (process.client && window.dataLayer && addUserRes.recorded !== false) {
        window.dataLayer.push({
          event: 'elker_conversion',
          'enhanced_conversion_email': email.value
        })
        window.dataLayer.push({
          event: 'contact_form_submitted'
        })
      }
    } else {
      // /api/elkerContact returns {ok:false} with HTTP 200 on a Pipedrive or
      // Mailgun failure, so $fetch does not throw and the catch below never
      // runs. Without this the attempt vanished from the funnel during an
      // outage, with no error row pointing at the server.
      errorMessage.value = addUserRes.message || 'There has been an error submitting the form. Please email us at hello@elker.com'
      formTracking.failed('server-rejected')
    }

  } catch(e) {

    if (!sent.value) {
      errorMessage.value = 'There has been an error submitting the form. Please email us at hello@elker.com'
      formTracking.failed('request-failed')
    }

  } finally {
    stopSendingMessages()
    waiting.value = null
  }

}

const { sendingMessage, startSendingMessages, stopSendingMessages } = useSubmitProgress()


const stats = [
  {
    title: 'Email',
    subtitle: 'hello@elker.com',
    icon: 'iconoir:mail-out'
  },
  {
    title: 'Address',
    subtitle: '6-14 Oxford St, Darlinghurst NSW Australia',
    icon: 'iconoir:pin-alt'
  }
]
</script>

<style lang="scss" scoped>
input[type='checkbox']:checked, 
input[type='radio']:checked {
background-color: #5a6ded!important;
}
</style>